import AppModal from "@components/modals/AppModal";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import "@assets/css/mode_tool/translatePointsModal.css";

function AutomateRoutePointsModal({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
  error,
  success,
  results = {},
  activeAction = "content",
  streamProgress = {},
  isSingleImagePoint = false,
  imagePointScope = "regular",
  onImagePointScopeChange,
}) {
  const actionLabels = {
    content: {
      title: "AI automate points",
      description: "Generate names, descriptions, and more info for regular route points.",
      progress: "Generating route point content...",
    },
    directions: {
      title: "AI direction points",
      description: "Generate names and descriptions for Direction points.",
      progress: "Generating Direction point content...",
    },
    parking: {
      title: "AI parking point",
      description: "Check whether this driving route needs a new parking point.",
      progress: "Checking route parking points...",
    },
    images: {
      title: "AI generate images",
      description: "Choose which route points should receive a generated image.",
      progress: "Generating route point images...",
    },
  };
  const action = actionLabels[activeAction] || actionLabels.content;
  const succeeded = Array.isArray(results.succeeded) ? results.succeeded : [];
  const failed = Array.isArray(results.failed) ? results.failed : [];
  const created = Array.isArray(results.created) ? results.created : [];
  const streamedPoints = Array.isArray(streamProgress.points) ? streamProgress.points : [];
  const total = Number(streamProgress.total) || streamedPoints.length;
  const completed = Number(streamProgress.completed) || 0;
  const progressPercent = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const statusCounts = streamedPoints.reduce((counts, item) => {
    const status = String(item?.status || "pending");
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});

  return (
    <AppModal isOpen={isOpen} onClose={onClose} closeDisabled={isGenerating} title={action.title} size="sm" className="mode-route-translate-modal">
      <p>{activeAction === "images" && isSingleImagePoint
        ? "Generate an image for this route point. Review the result before saving."
        : action.description}</p>
      {activeAction === "images" && !isSingleImagePoint && !streamProgress.completeReceived ? (
        <fieldset className="mode-route-translate-languages mode-route-ai-point-scope" disabled={isGenerating}>
          <legend>Points to generate images for</legend>
          {[
            ["all", "All points"],
            ["regular", "Regular points only"],
            ["directions", "Direction points only"],
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name="ai-image-point-scope"
                value={value}
                checked={imagePointScope === value}
                onChange={() => onImagePointScopeChange?.(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {isGenerating ? <ThinkingAnimation label={action.progress} /> : null}
      {(["content", "directions", "images"].includes(activeAction)) && streamedPoints.length ? (
        <section className={`mode-route-ai-stream ${isGenerating ? "is-running" : "is-finished"}`} aria-live="polite">
          <div className="mode-route-ai-stream-heading">
            <strong>{streamProgress.done ? "Generation summary" : activeAction === "images" ? "Generating route images" : "Generating point content"}</strong>
            <span>{completed} / {total} {activeAction === "images" ? "images" : "points"}</span>
          </div>
          <div
            className="mode-route-ai-stream-track"
            role="progressbar"
            aria-label={activeAction === "images" ? "AI image generation progress" : "AI point generation progress"}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={Math.min(completed, total)}
          >
            <span style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="mode-route-ai-stream-counts">
            <span className="is-completed">{statusCounts.completed || 0} {activeAction === "images" ? "images generated" : "generated"}</span>
            <span className="is-skipped">{statusCounts.skipped || 0} skipped</span>
            <span className="is-error">{statusCounts.error || 0} errors</span>
          </div>
          <ol className="mode-route-ai-stream-list">
            {streamedPoints.map((item, index) => {
              const status = String(item?.status || "pending");
              const pointName = readPointName(item?.point?.name);
              const imageValue = Array.isArray(item?.point?.images) ? item.point.images[0] : null;
              const imageUrl = typeof imageValue === "string" ? imageValue : String(imageValue?.url || "");
              const statusLabel = {
                completed: "Generated",
                skipped: "Skipped",
                error: "Error",
                pending: "Waiting",
              }[status] || status;
              const statusIcon = {
                completed: "✓",
                skipped: "–",
                error: "!",
                pending: "…",
              }[status] || "•";
              return (
                <li className={`is-${status}`} key={item?.id || index}>
                  <span className="mode-route-ai-stream-status" aria-hidden="true">{statusIcon}</span>
                  {activeAction === "images" && imageUrl ? (
                    <img className="mode-route-ai-image-preview" src={imageUrl} alt={pointName || `Generated image for point ${index + 1}`} />
                  ) : null}
                  <span className="mode-route-ai-stream-point">
                    <strong>#{index + 1}{pointName ? ` · ${pointName}` : ""}</strong>
                    {item?.message ? <small>{item.message}</small> : null}
                  </span>
                  <span className="mode-route-ai-stream-label">{statusLabel}</span>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}
      {error ? <p className="message-error" role="alert">{error}</p> : null}
      {(activeAction === "parking" || activeAction === "images") && success ? <p className="mode-route-translate-success" role="status">{success}</p> : null}
      {activeAction === "parking" && succeeded.length ? (
        <ResultList title="Content generated" points={succeeded} variant="success" />
      ) : null}
      {activeAction === "parking" && created.length ? (
        <CreatedParkingPoints points={created} />
      ) : null}
      {activeAction === "parking" && failed.length ? (
        <ResultList title="Points that failed" points={failed} variant="error" />
      ) : null}
      <div className="tour-add-editor-actions">
        <button type="button" className="button-ghost" onClick={onClose} disabled={isGenerating}>
          Close
        </button>
        {!streamProgress.completeReceived ? (
          <button type="button" className="button-primary" onClick={() => onGenerate?.()} disabled={isGenerating}>
            {isGenerating ? "Generating..." : "Generate"}
          </button>
        ) : null}
      </div>
    </AppModal>
  );
}

function readPointName(value) {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  if (typeof value.en === "string") return value.en.trim();
  if (typeof value.en?.value === "string") return value.en.value.trim();
  if (Array.isArray(value)) {
    const englishEntry = value.find((entry) =>
      Object.keys(entry || {}).some((language) => language.toLowerCase() === "en"),
    );
    const englishKey = Object.keys(englishEntry || {}).find(
      (language) => language.toLowerCase() === "en",
    );
    const englishValue = englishEntry?.[englishKey];
    return String(englishValue?.value ?? englishValue ?? "").trim();
  }
  return "";
}

function ResultList({ title, points, variant }) {
  return (
    <section
      className={`mode-route-translate-results ${variant === "error" ? "mode-route-translate-failures" : "mode-route-translate-results-success"}`}
      role={variant === "error" ? "alert" : "status"}
    >
      <p>{title}:</p>
      <ul>{points.map((point) => <li key={point.id}>{point.label}</li>)}</ul>
    </section>
  );
}

function CreatedParkingPoints({ points }) {
  return (
    <section className="mode-route-translate-results mode-route-translate-results-success mode-route-parking-created" role="status">
      <p>New route points added:</p>
      <ul>
        {points.map((point, index) => {
          const number = Number(point?.order) || index + 1;
          return (
            <li key={point?.id || index} className="mode-route-parking-created-item">
              <span
                className="mode-route-parking-created-pin"
                role="img"
                aria-label={`Point ${number}`}
                title={`Point ${number}`}
              >
                <span>{number}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default AutomateRoutePointsModal;
